// @spec GHG-STR-030
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowLeft,
  Calendar,
  CheckCircle,
  Edit2,
  Loader2,
  ShieldCheck,
  Target,
  Trash2,
  TrendingDown,
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useApproveEmissionTarget,
  useDeleteEmissionTarget,
  useEmissionTarget,
  useUpdateEmissionTarget,
} from '@/features/ghg/api/use-emission-targets';
import { EmissionTargetForm } from '@/features/ghg/components/EmissionTargetForm';
import { TargetAlertConfig } from '@/features/ghg/components/targets/TargetAlertConfig';
import { TargetAlertHistory } from '@/features/ghg/components/targets/TargetAlertHistory';
import type { EmissionTargetFormValues } from '@/features/ghg/types';
import { formatLocalizedDate } from '@/lib/date-locale';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/targets/$targetId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.targetDetails'),
  }),
  component: EmissionTargetDetailPage,
});

/**
 * Emission target detail page.
 *
 * Displays comprehensive target details including:
 * - Target metadata and configuration
 * - Progress tracking
 * - Milestones timeline
 * - Edit and delete actions
 */
function EmissionTargetDetailPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { targetId } = Route.useParams();

  const { target, isLoading, isError } = useEmissionTarget(targetId);
  const updateTarget = useUpdateEmissionTarget();
  const deleteTarget = useDeleteEmissionTarget();
  const approveTarget = useApproveEmissionTarget();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const handleBack = () => {
    void navigate({ to: '/ghg/targets', search: { section: 'targets' } });
  };

  const handleEdit = (data: EmissionTargetFormValues) => {
    updateTarget.mutate(
      {
        targetId,
        data: {
          name: data.name,
          targetReductionPercent: parseFloat(data.targetReductionPercent),
          scopeCoverage: data.scopeCoverage,
          scienceBased: data.scienceBased,
          sbtiClassification: data.sbtiClassification || undefined,
          milestones: data.milestones,
          notes: data.notes || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success(t('toast.targetUpdatedSuccessfully'));
          setIsEditOpen(false);
        },
        onError: () => {
          toast.error(t('toast.failedToUpdateTarget'));
        },
      }
    );
  };

  const handleDelete = () => {
    deleteTarget.mutate(targetId, {
      onSuccess: () => {
        toast.success(t('toast.targetDeletedSuccessfully'));
        void navigate({ to: '/ghg/targets', search: { section: 'targets' } });
      },
      onError: () => {
        toast.error(t('toast.failedToDeleteTargetItMayHaveProgressRecordsAttached'));
      },
    });
  };

  const handleApprove = () => {
    approveTarget.mutate(targetId, {
      onSuccess: () => {
        toast.success(t('toast.targetApprovedSuccessfully'));
      },
      onError: () => {
        toast.error(t('toast.failedToApproveTarget'));
      },
    });
  };

  const formatNumber = (value: number | null | undefined): string => {
    if (value == null) return '-';
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  };

  const formatDate = (dateString: string): string => {
    return formatLocalizedDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !target) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('targetDetail.back')}
          </Button>
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">{t('targetDetail.notFound')}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const absoluteReductionTarget = (target.baseYearEmissions * target.targetReductionPercent) / 100;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
              <ArrowLeft className="mr-1 h-4 w-4" />
              {t('targetDetail.back')}
            </Button>
            <div className="flex items-center gap-3">
              <Target className="text-primary h-6 w-6" />
              <h1 className="text-2xl font-semibold">{target.name}</h1>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={target.status === 'active' ? 'default' : 'secondary'}>
                {t(`targetDetail.status_${target.status}`)}
              </Badge>
              {target.scienceBased ? (
                <Badge variant="outline" className="gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  {t('targetDetail.sbti')} {target.sbtiClassification}
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {target.status === 'active' && !target.approvedAt ? (
              <Button variant="outline" onClick={handleApprove} disabled={approveTarget.isPending}>
                {approveTarget.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle className="mr-2 h-4 w-4" />
                )}
                {t('targetDetail.approve')}
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() => {
                setIsEditOpen(true);
              }}
            >
              <Edit2 className="mr-2 h-4 w-4" />
              {t('targetDetail.edit')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setIsDeleteOpen(true);
              }}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {t('targetDetail.delete')}
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('targetDetail.baseYear')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{target.baseYear}</div>
              <p className="text-muted-foreground text-xs">
                {formatNumber(target.baseYearEmissions)} {t('targetDetail.tCO2e')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('targetDetail.targetYear')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{target.targetYear}</div>
              <p className="text-muted-foreground text-xs">
                {formatNumber(target.targetEmissions)} {t('targetDetail.tCO2e')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">{t('targetDetail.reductionTarget')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-green-600" />
                <span className="text-2xl font-bold">
                  {target.targetType === 'absolute'
                    ? `${formatNumber(absoluteReductionTarget)} tCO2e`
                    : `${formatNumber(target.targetReductionPercent)}%`}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scope Coverage */}
        <Card>
          <CardHeader>
            <CardTitle>{t('targetDetail.scopeCoverage')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {target.scopeCoverage.map((scope) => (
                <Badge key={scope} variant="secondary">
                  {scope.replace('scope', `${t('targetDetail.scopePrefix')} `)}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Milestones */}
        {target.milestones.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('targetDetail.interimMilestones')}</CardTitle>
              <CardDescription>{t('targetDetail.milestonesDescription')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {target.milestones.map((milestone, index) => (
                  <div key={String(index)} className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="text-muted-foreground h-4 w-4" />
                      <span className="font-medium">{milestone.year}</span>
                    </div>
                    <div className="flex-1">
                      <Progress value={milestone.targetReductionPercent} className="h-2" />
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{milestone.targetReductionPercent}%</div>
                      <div className="text-muted-foreground text-xs">
                        {formatNumber(milestone.targetEmissions)} {t('targetDetail.tCO2e')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Alert Configuration */}
        <TargetAlertConfig targetId={targetId} />

        {/* Alert History */}
        <TargetAlertHistory targetId={targetId} />

        {/* Notes */}
        {target.notes ? (
          <Card>
            <CardHeader>
              <CardTitle>{t('targetDetail.notes')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground whitespace-pre-wrap">{target.notes}</p>
            </CardContent>
          </Card>
        ) : null}

        {/* Metadata */}
        <Card>
          <CardHeader>
            <CardTitle>{t('targetDetail.details')}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 text-sm md:grid-cols-2">
            <div>
              <span className="text-muted-foreground">{t('targetDetail.created')}:</span>
              <span className="ml-2">{formatDate(target.createdAt)}</span>
              {target.createdBy ? (
                <span className="text-muted-foreground ml-1">{t('targetDetail.by')} {target.createdBy.name}</span>
              ) : null}
            </div>
            {target.approvedAt ? (
              <div>
                <span className="text-muted-foreground">{t('targetDetail.approved')}:</span>
                <span className="ml-2">{formatDate(target.approvedAt)}</span>
                {target.approvedBy ? (
                  <span className="text-muted-foreground ml-1">{t('targetDetail.by')} {target.approvedBy.name}</span>
                ) : null}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('targetDetail.editTitle')}</DialogTitle>
            <DialogDescription>
              {t('targetDetail.editDescription')}
            </DialogDescription>
          </DialogHeader>
          <EmissionTargetForm
            target={target}
            onSubmit={handleEdit}
            onCancel={() => {
              setIsEditOpen(false);
            }}
            isSubmitting={updateTarget.isPending}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('targetDetail.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('targetDetail.deleteDescription', { name: target.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('targetDetail.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTarget.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-2 h-4 w-4" />
              )}
              {t('targetDetail.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
